import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Palette as PaletteIcon,
  TextFields as TypographyIcon,
  Straighten as SpacingIcon,
  Crop as LayoutIcon,
  Tune as ComponentsIcon,
  BrokenImage as ImagesIcon,
  ColorLens as ColorIcon,
  FormatSize as FontIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import DocumentTypeTemplate from '../DocumentTypeTemplate';

const DesignGuidelines = ({ projectId, existingContent = '', onSave }) => {
  const renderDesignGuidelinesPreview = (content) => {
    // Try to parse as JSON first
    try {
      const designData = JSON.parse(content);
      return renderStructuredDesign(designData);
    } catch (e) {
      // If not JSON, render as markdown
      return (
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
          <ReactMarkdown>{content}</ReactMarkdown>
        </Box>
      );
    }
  };
  
  // Render a structured design system visualization
  const renderStructuredDesign = (designData) => {
    return (
      <Box>
        <Grid container spacing={3}>
          {/* Typography Section */}
          {designData.typography && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TypographyIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Typography</Typography>
                  </Box>
                  
                  {designData.typography.fontFamily && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Font Family
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: designData.typography.fontFamily }}>
                        {designData.typography.fontFamily}
                      </Typography>
                    </Box>
                  )}
                  
                  {designData.typography.headings && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Headings
                      </Typography>
                      <Typography variant="h4" sx={{ fontFamily: designData.typography.fontFamily }}>
                        H1: {designData.typography.headings.h1}
                      </Typography>
                      <Typography variant="h5" sx={{ fontFamily: designData.typography.fontFamily }}>
                        H2: {designData.typography.headings.h2}
                      </Typography>
                      <Typography variant="h6" sx={{ fontFamily: designData.typography.fontFamily }}>
                        H3: {designData.typography.headings.h3}
                      </Typography>
                    </Box>
                  )}
                  
                  {designData.typography.body && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Body Text
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: designData.typography.fontFamily }}>
                        Body: {designData.typography.body.size}
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: designData.typography.fontFamily }}>
                        Small: {designData.typography.body.small}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Colors Section */}
          {designData.colors && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PaletteIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Color Palette</Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    {designData.colors.primary && (
                      <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                          <Box 
                            sx={{ 
                              height: 50, 
                              bgcolor: designData.colors.primary, 
                              borderRadius: 1,
                              mb: 1
                            }} 
                          />
                          <Typography variant="body2">
                            Primary: {designData.colors.primary}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    
                    {designData.colors.secondary && (
                      <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                          <Box 
                            sx={{ 
                              height: 50, 
                              bgcolor: designData.colors.secondary, 
                              borderRadius: 1,
                              mb: 1
                            }} 
                          />
                          <Typography variant="body2">
                            Secondary: {designData.colors.secondary}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    
                    {designData.colors.accent && (
                      <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                          <Box 
                            sx={{ 
                              height: 50, 
                              bgcolor: designData.colors.accent, 
                              borderRadius: 1,
                              mb: 1
                            }} 
                          />
                          <Typography variant="body2">
                            Accent: {designData.colors.accent}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    
                    {designData.colors.background && (
                      <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                          <Box 
                            sx={{ 
                              height: 50, 
                              bgcolor: designData.colors.background, 
                              borderRadius: 1,
                              border: '1px solid #e0e0e0',
                              mb: 1
                            }} 
                          />
                          <Typography variant="body2">
                            Background: {designData.colors.background}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Spacing Section */}
          {designData.spacing && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SpacingIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Spacing</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Object.entries(designData.spacing).map(([key, value]) => (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ width: 100 }}>
                          {key}: {value}
                        </Typography>
                        <Box 
                          sx={{ 
                            height: 20, 
                            width: value.replace('px', '') + 'px',
                            bgcolor: 'primary.main',
                            borderRadius: 0.5,
                            minWidth: 4
                          }} 
                        />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {/* Components Section */}
          {designData.components && (
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ComponentsIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Components</Typography>
                  </Box>
                  
                  <List dense>
                    {Object.entries(designData.components).map(([key, value]) => (
                      <ListItem key={key}>
                        <ListItemIcon>
                          <ComponentsIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={key} 
                          secondary={typeof value === 'string' ? value : JSON.stringify(value)} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };
  
  // Design guidelines prompt configuration
  const designGuidelinesPromptConfig = {
    system: "You are a UI/UX design expert with expertise in design systems, accessibility, and user interface design principles.",
    examples: [
      "Create a color palette for our healthcare application",
      "What typography guidelines should we follow for a financial app?"
    ],
    instruction: "Create detailed design guidelines including typography, colors, spacing, and component specifications."
  };
  
  return (
    <DocumentTypeTemplate
      projectId={projectId}
      documentType="designGuidelines"
      title="Design Guidelines"
      icon={PaletteIcon}
      existingContent={existingContent}
      onSave={onSave}
      renderPreview={renderDesignGuidelinesPreview}
      promptConfig={designGuidelinesPromptConfig}
      allowedImportTypes="application/json,text/plain,text/markdown"
      useSplitView={true}
    >
      {/* Additional design guidelines content */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Design System Components</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ColorIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Color System</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Define primary, secondary, and accent colors with proper contrast ratios.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ height: 20, width: 20, bgcolor: '#1976d2', borderRadius: '50%' }} />
                  <Box sx={{ height: 20, width: 20, bgcolor: '#dc004e', borderRadius: '50%' }} />
                  <Box sx={{ height: 20, width: 20, bgcolor: '#f50057', borderRadius: '50%' }} />
                  <Box sx={{ height: 20, width: 20, bgcolor: '#00e676', borderRadius: '50%' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FontIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Typography</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Consistent typography system with clear hierarchy.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Heading</Typography>
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>Subtitle</Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.2 }}>Body text</Typography>
                  <Typography variant="caption" sx={{ lineHeight: 1.2 }}>Caption</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SpacingIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Spacing</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Consistent spacing system for visual harmony.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ height: 4, width: 8, bgcolor: '#1976d2' }} />
                  <Box sx={{ height: 4, width: 16, bgcolor: '#1976d2' }} />
                  <Box sx={{ height: 4, width: 24, bgcolor: '#1976d2' }} />
                  <Box sx={{ height: 4, width: 32, bgcolor: '#1976d2' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Accessibility Guidelines</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Color & Contrast
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <ImagesIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Maintain 4.5:1 contrast ratio for regular text" 
                  secondary="Ensure text is readable on all backgrounds" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ImagesIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Use color as enhancement, not sole indicator" 
                  secondary="Always provide additional visual cues beyond color" 
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Interface Elements
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <LayoutIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Provide visible focus indicators" 
                  secondary="Ensure keyboard navigation is visually indicated" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LayoutIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Use semantic HTML" 
                  secondary="Proper headings, buttons, and form controls" 
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Design System Tools
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label="Figma" variant="outlined" />
            <Chip label="Storybook" variant="outlined" />
            <Chip label="Zeplin" variant="outlined" />
            <Chip label="InVision" variant="outlined" />
            <Chip label="Adobe XD" variant="outlined" />
            <Chip label="Sketch" variant="outlined" />
          </Box>
        </Box>
      </Paper>
    </DocumentTypeTemplate>
  );
};

export default DesignGuidelines;